import { useModalState } from "@clipboard-health/ui-components";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import { ShiftBlockStatus, useUpdateShiftBlock } from "./api/useUpdateBlockShift";
import { CancelShiftBlockBottomSheet } from "./CancelShiftBlockBottomSheet";
import { CancelShiftBlockIconButton } from "./CancelShiftBlockIconButton";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

interface Props {
  shiftBlockId: string;
  facilityId: string;
  totalShifts: number;
  isShiftBlockAssigned: boolean;
  onConfirmCancellation: () => void;
}

export function CancelShiftBlockAction(props: Props) {
  const { shiftBlockId, facilityId, totalShifts, isShiftBlockAssigned, onConfirmCancellation } =
    props;
  const cancelShiftBlockModalState = useModalState();
  const { showErrorToast, showInfoToast } = useToast();
  const { path: currentRouterUrl } = useRouteMatch();
  const [isCancellationSubmitting, setIsCancellationSubmitting] = useState(false);
  const { mutateAsync: mutateCancelShiftBlock, isLoading: isLoadingCancelShiftBlock } =
    useUpdateShiftBlock({
      shiftBlockId,
    });
  const isCancellationInFlight = isCancellationSubmitting || isLoadingCancelShiftBlock;

  const handleOpenCancelShiftBlock = useCallback(() => {
    if (isCancellationInFlight) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.CANCEL_BLOCK_TAPPED,
      shiftBlockId,
      facilityId,
      location: currentRouterUrl,
    });
    cancelShiftBlockModalState.openModal();
  }, [
    cancelShiftBlockModalState,
    currentRouterUrl,
    facilityId,
    isCancellationInFlight,
    shiftBlockId,
  ]);

  const handleConfirmCancellation = useCallback(async () => {
    if (isCancellationInFlight) {
      return;
    }

    setIsCancellationSubmitting(true);

    try {
      await mutateCancelShiftBlock({
        data: {
          type: "shift-block",
          id: shiftBlockId,
          attributes: {
            status: ShiftBlockStatus.CANCELLED,
          },
        },
      });
      showInfoToast("Shift block canceled successfully.");
      onConfirmCancellation();
    } catch {
      showErrorToast("Something went wrong while canceling the shift block.");
    } finally {
      setIsCancellationSubmitting(false);
    }
  }, [
    isCancellationInFlight,
    mutateCancelShiftBlock,
    onConfirmCancellation,
    shiftBlockId,
    showErrorToast,
    showInfoToast,
  ]);

  return (
    <>
      <CancelShiftBlockIconButton
        isShiftBlockAssigned={isShiftBlockAssigned}
        disabled={isCancellationInFlight}
        onClick={handleOpenCancelShiftBlock}
      />
      {isShiftBlockAssigned && (
        <CancelShiftBlockBottomSheet
          modalState={cancelShiftBlockModalState}
          shiftBlockId={shiftBlockId}
          facilityId={facilityId}
          totalShifts={totalShifts}
          onConfirmCancellation={() => {
            void handleConfirmCancellation();
          }}
        />
      )}
    </>
  );
}
